import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { Input, Form, Button, Select, notification, Flex } from 'antd';
import { jwtDecode } from 'jwt-decode';

import ResetPassword from '@controls/reset-password/reset-password';

import { exception } from '@extensions/notification';

import { useAppDispatch } from '@store/hooks';
import { userLoaded } from '@store/actions';
import { isNewYearAroundUs } from '@extensions/utils';
import { getEnumList, tryParseJson } from '@extensions/utils';

import { IUserSession } from '@entities/user-session';
import { IContact } from '@entities/contact';

import { UserType } from '@enums/user-type';
import { BusinessUnit, enumLabel } from '@enums/business-unit';
import { IEnumItem } from '@enums/enum-item';

import { MainLogoIcon } from '@icons/index';

import GooglePlay from '@assets/img/google-play-badge.png';
import GooglePlayQr from '@assets/img/GooglePlayQr.png';
import AppStoreQr from '@assets/img/AppStoreQr.png';

import MaxCuttingBotQr from '@assets/svg/MaxCuttingBotQr.svg';
import MaxPlantingBotQr from '@assets/svg/MaxPlantingBotQr.svg';
import TelegramCuttingBotQr from '@assets/svg/TelegramCuttingBotQr.svg';
import TelegramPlantingBotQr from '@assets/svg/TelegramPlantingBotQr.svg';
import Telegram from '@assets/svg/Telegram.svg';
import WhatsApp from '@assets/svg/WhatsApp.svg';
import AppStore from '@assets/svg/app-store-badge.svg';

import { serverFetch } from '@core/server';

import '@core/auth.css';

const flcApiBaseUrl = process.env.REACT_APP_FLC_API_BASE_URL;
const flpApiBaseUrl = process.env.REACT_APP_FLP_API_BASE_URL;

interface IAuth {
    appVersion: string | undefined;
}

interface IAuthParams {
    businessUnit: BusinessUnit;
    login: string;
    password: string;
}

const Auth = (props: IAuth) => {
    const { appVersion } = props;

    const navigate = useNavigate();

    const d = useAppDispatch();

    const [form] = Form.useForm();
    const businessUnit = Form.useWatch<BusinessUnit>('businessUnit', form);

    const [api, contextHolder] = notification.useNotification();

    const [apiBaseUrl, setApiBaseUrl] = useState<string>();
    const [loading, setLoading] = useState<boolean>(false);
    const [systemSupport, setSystemSupport] = useState<IContact>();

    const [showResetPassword, setShowResetPassword] = useState<boolean>(false);

    const [businessUnits] = useState<Array<IEnumItem>>(getEnumList(BusinessUnit, enumLabel));

    useEffect(() => {
        const storedBusinessUnit = localStorage.getItem('businessUnit');
        if (storedBusinessUnit) {
            const businessUnit: BusinessUnit = +storedBusinessUnit as BusinessUnit;
            form.setFieldValue('businessUnit', businessUnit);
        }

        getSystemSupport();

        document.addEventListener('keydown', onKeyDown, false);
        return () => {
            document.removeEventListener('keydown', onKeyDown, false);
            close();
        };
    }, []);

    useEffect(() => {
        setApiBaseUrl(businessUnit == BusinessUnit.Cutting ? flcApiBaseUrl : flpApiBaseUrl);
    }, [businessUnit]);

    const getSystemSupport = () => {
        serverFetch(flcApiBaseUrl, undefined, `contacts/systemsupport`, { method: 'GET' })
            .then((data) => {
                setSystemSupport(data);
            })
            .catch((ex) => {
                exception(api, 'Ошибка получения контакта технической поддержки', ex, () => d(userLoaded(undefined)));
            });
    };

    const onKeyDown = (e: any) => {
        if (e.keyCode === 13) {
            //signInForm.submit();
        }
    };

    const close = () => {
        setLoading(false);
        form.resetFields();
    };

    const onFinish = (entity: IAuthParams) => {
        setLoading(true);

        const data = {
            businessUnit: entity.businessUnit,
            login: entity.login,
            password: entity.password,
            isWeb: true,
        };

        const apiBaseUrl = entity.businessUnit == BusinessUnit.Cutting ? flcApiBaseUrl : flpApiBaseUrl;

        serverFetch(apiBaseUrl, undefined, `auth/mysignin`, { method: 'POST', bodyData: data, credentials: 'include' })
            .then((data) => {
                window.accessToken = data.accessToken;

                localStorage.setItem('refreshToken', data.refreshToken);

                const result: any = jwtDecode(data.accessToken);

                let storedBusinessUnit = localStorage.getItem('businessUnit');
                if (!storedBusinessUnit || +storedBusinessUnit != result.businessUnit) {
                    localStorage.setItem('businessUnit', result.businessUnit as any);
                    navigate('/');
                }

                const userSession: IUserSession = {
                    businessUnit: result.businessUnit,
                    userId: result.id,
                    fullName: result.fullName,
                    type: +result.type as UserType,
                    permissions: tryParseJson(result.permissions),
                    userSettings: tryParseJson(result.userSettings),

                    resetPasswordRequired: data.resetPasswordRequired,
                    isDemo: data.isDemo,

                    useTagRequired: result.useTagRequired == 'True',
                    tag: result.tag,
                };

                d(userLoaded(userSession));
                close();
            })
            .catch((ex) => {
                setLoading(false);
                exception(api, 'Ошибка авторизации', ex, () => d(userLoaded(undefined)));
            });
    };

    return (
        <div>
            <div className='auth'>
                <div className='auth-qr-container'>
                    <div>
                        <a href='https://play.google.com/store/apps/details?id=ru.freshlinecargo.my' target='_blank'>
                            <img style={{ width: 170 }} alt='Доступно в Google Play' src={GooglePlay} />
                        </a>
                        <div>
                            <img src={GooglePlayQr} style={{ width: 150 }} />
                        </div>
                    </div>
                    <div style={{ marginLeft: 30 }}>
                        <a href='https://apps.apple.com/app/my-freshline/id6642688980' target='_blank'>
                            <img style={{ width: 170, height: 50, margin: 8 }} alt='Доступно на  App Store' src={AppStore} />
                        </a>
                        <div>
                            <img src={AppStoreQr} style={{ width: 150 }} />
                        </div>
                    </div>
                </div>

                <Flex className='auth-container' vertical justify='space-between' align='center'>
                    <div className='auth-form'>
                        <div className='auth-logo'>
                            {isNewYearAroundUs() && <div className='santa-hat-auth'></div>}
                            <MainLogoIcon style={{ fontSize: 64 }} />
                        </div>
                        <div className='auth-form-title'>Личный кабинет</div>

                        <Form size={'large'} form={form} initialValues={{ remember: true }} onFinish={onFinish}>
                            <Form.Item name='businessUnit' rules={[{ required: true, message: 'Выберите приложение' }]}>
                                <Select
                                    styles={{
                                        root: {
                                            textAlign: 'center',
                                            fontWeight: 700,
                                            color: 'var(--main-green)',
                                            textTransform: 'uppercase',
                                            justifyContent: 'center',
                                        },
                                        content: {
                                            justifyContent: 'center',
                                        },
                                        popup: {
                                            root: {
                                                backgroundColor: '#E5E5FF',
                                                textTransform: 'uppercase',
                                                textAlign: 'center',
                                            },
                                        },
                                    }}
                                    placeholder='Приложение'
                                    onChange={(value: BusinessUnit) => {
                                        form.setFieldValue('businessUnit', value);
                                    }}
                                    options={businessUnits.map((t) => {
                                        return { value: t.value, label: t.label };
                                    })}
                                />
                            </Form.Item>

                            <Form.Item
                                name='login'
                                rules={[{ required: true, message: 'Пожалуйста введите логин' }]}
                                hasFeedback
                                className='auth-form-item'
                            >
                                <Input name='login' placeholder='Логин' disabled={loading} className='auth-form-item-input' />
                            </Form.Item>
                            <Form.Item
                                name='password'
                                className='auth-form-item'
                                rules={[{ required: true, message: 'Пожалуйста введите пароль' }]}
                                hasFeedback
                            >
                                <Input.Password
                                    name='password'
                                    placeholder={'Пароль'}
                                    disabled={loading}
                                    className='auth-form-item-input'
                                />
                            </Form.Item>
                            <Form.Item style={{ marginTop: -12, marginLeft: -10, marginBottom: 0, textAlign: 'left' }}>
                                <Button type='link' onClick={() => setShowResetPassword(true)} style={{ color: 'var(--primary-color)' }}>
                                    Не помню пароль
                                </Button>
                            </Form.Item>
                            <Form.Item style={{ marginBottom: 0 }}>
                                <Button type='primary' htmlType='submit' loading={loading} className='auth-form-btn'>
                                    Войти
                                </Button>
                            </Form.Item>

                            {businessUnit == BusinessUnit.Cutting ? (
                                <Form.Item style={{ marginTop: 40, textAlign: 'center' }}>
                                    <Flex gap='large' align='flex-start' justify='center'>
                                        <a href='https://t.me/FreshLineCuttingBot' target='_blank'>
                                            <img style={{ width: 160 }} alt='Доступно в Telegram' src={TelegramCuttingBotQr} />
                                        </a>
                                        <a href='https://max.ru/id9721145312_1_bot' target='_blank' style={{ color: '#914cf8' }}>
                                            <div
                                                style={{
                                                    maxWidth: 160,
                                                    maxHeight: 160,
                                                    background: '#ffffff',
                                                    padding: '15px 5px 0',
                                                    borderRadius: 7,
                                                }}
                                            >
                                                <img style={{ width: 128, marginBottom: -10 }} alt='Доступно в MAX' src={MaxCuttingBotQr} />
                                                <span style={{ fontSize: 11 }}>MAXBOTCUTTING</span>
                                            </div>
                                        </a>
                                    </Flex>
                                </Form.Item>
                            ) : (
                                <Form.Item style={{ marginTop: 40, textAlign: 'center' }}>
                                    <Flex gap='large' align='flex-start' justify='center'>
                                        <a href='https://t.me/FreshLinePlantingBot' target='_blank'>
                                            <img style={{ width: 160 }} alt='Доступно в Telegram' src={TelegramPlantingBotQr} />
                                        </a>
                                        <a href='https://max.ru/id9721145312_2_bot' target='_blank' style={{ color: '#914cf8' }}>
                                            <div
                                                style={{
                                                    maxWidth: 160,
                                                    maxHeight: 160,
                                                    background: '#ffffff',
                                                    padding: '15px 5px 0',
                                                    borderRadius: 7,
                                                }}
                                            >
                                                <img
                                                    style={{ width: 128, marginBottom: -10 }}
                                                    alt='Доступно в MAX'
                                                    src={MaxPlantingBotQr}
                                                />
                                                <span style={{ fontSize: 11 }}>MAXBOTPLANTING</span>
                                            </div>
                                        </a>
                                    </Flex>
                                </Form.Item>
                            )}
                        </Form>
                    </div>

                    {systemSupport?.phone && (
                        <div className='auth-support-container'>
                            <div className='auth-support'>
                                {systemSupport?.workingPosition ?? 'Сервисная поддержка1'}
                                <div className='auth-support-phone'>
                                    <Flex align='center' justify='center'>
                                        <img style={{ width: 50 }} alt='Telegram' src={Telegram} />
                                        <img style={{ width: 50, marginLeft: -20 }} alt='WhatsApp' src={WhatsApp} />
                                        {systemSupport.phone}
                                    </Flex>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className='auth-app-version'>Версия {appVersion}</div>
                </Flex>

                {showResetPassword && <ResetPassword api={api} apiBaseUrl={apiBaseUrl} onClose={() => setShowResetPassword(false)} />}

                {contextHolder}
            </div>
        </div>
    );
};

export default Auth;
